import {
  AspectRatio,
  Box,
  Flex,
  Icon,
  IconButton,
  Image,
  Spinner,
  Text,
  VStack,
  keyframes,
  useToast,
} from '@chakra-ui/react';
import { FaCamera } from '@react-icons/all-files/fa/FaCamera';
import { FaCar } from '@react-icons/all-files/fa/FaCar';
import { FaCog } from '@react-icons/all-files/fa/FaCog';
import { FaTrash } from '@react-icons/all-files/fa/FaTrash';
import { GiCarWheel } from '@react-icons/all-files/gi/GiCarWheel';
import { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';

import { PhotoEntry } from '../buildPhotoSlotMap';
import { PHOTO_INPUT_ACCEPT, PhotoCategory, SlotDefinition } from '../constants';

const CATEGORY_ICON: Record<PhotoCategory, typeof FaCar> = {
  exterior: FaCar,
  interior: FaCamera,
  engineBay: FaCog,
  wheelsTires: GiCarWheel,
};

const pulseRing = keyframes`
  0% { box-shadow: 0 0 0 0 var(--chakra-colors-oceanBoatBlue); }
  70% { box-shadow: 0 0 0 6px transparent; }
  100% { box-shadow: 0 0 0 0 transparent; }
`;

type PhotoSlotProps = {
  slot: SlotDefinition;
  photo: PhotoEntry | undefined;
  isProcessing: boolean;
  isPulsing: boolean;
  onClick: () => void;
  onViewPhoto: () => void;
  onDelete: () => void;
  onFileDrop: (file: File) => void;
  slotRef: (el: HTMLDivElement | null) => void;
  isPostAcceptance?: boolean;
};

const PhotoSlot = ({
  slot,
  photo,
  isProcessing,
  isPulsing,
  onClick,
  onViewPhoto,
  onDelete,
  onFileDrop,
  slotRef,
  isPostAcceptance = false,
}: PhotoSlotProps) => {
  const isEmpty = !photo && !isProcessing;
  const toast = useToast();

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];
      if (file) onFileDrop(file);
    },
    [onFileDrop],
  );

  const onDropRejected = useCallback(() => {
    toast({
      position: 'top',
      duration: 4000,
      isClosable: true,
      title: 'Unsupported file type',
      description: 'Please drop an image file (e.g. JPEG, PNG, WebP, HEIC).',
      status: 'error',
    });
  }, [toast]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    onDropRejected,
    accept: PHOTO_INPUT_ACCEPT,
    maxFiles: 1,
    noClick: true,
    noKeyboard: true,
  });

  const handleClick = () => {
    if (isProcessing) {
      return;
    }

    if (photo) {
      onViewPhoto();
    } else if (isEmpty) {
      onClick();
    }
  };

  return (
    <AspectRatio ratio={4 / 3}>
      <Box
        {...getRootProps()}
        ref={(el: HTMLDivElement | null) => {
          slotRef(el);
        }}
        borderRadius="10px"
        border={isDragActive ? '2px solid' : photo ? '2px solid' : '2px dashed'}
        borderColor={
          isDragActive
            ? 'oceanBoatBlue'
            : photo
            ? 'transparent'
            : isProcessing
            ? 'oceanBoatBlue'
            : 'grayDarkBackground'
        }
        bg={
          isDragActive
            ? 'iceBlue'
            : photo
            ? 'transparent'
            : isProcessing
            ? 'iceBlue'
            : 'grayBackground'
        }
        cursor="pointer"
        overflow="hidden"
        position="relative"
        transition="all 0.2s ease"
        animation={isPulsing ? `${pulseRing} 1.5s ease-in-out 2` : undefined}
        onClick={handleClick}
        role="button"
        aria-label={photo ? `View ${slot.label} photo` : `Upload ${slot.label} photo`}
        aria-busy={isProcessing}
        _hover={
          isEmpty && !isDragActive && !isProcessing
            ? { borderColor: 'oceanBoatBlue', bg: 'iceBlue' }
            : {}
        }
      >
        <input {...getInputProps()} />

        {isDragActive && (
          <Flex position="absolute" inset={0} alignItems="center" justifyContent="center">
            <VStack spacing={1.5}>
              <Icon as={FaCamera} boxSize="24px" color="oceanBoatBlue" />
              <Text fontSize="12px" fontWeight="700" color="oceanBoatBlue" textAlign="center">
                Drop here
              </Text>
            </VStack>
          </Flex>
        )}

        {!isDragActive && isEmpty && (
          <VStack
            position="absolute"
            inset={0}
            spacing={1.5}
            py={{ base: 2, md: 0 }}
            justify={{ base: 'space-between', md: 'center' }}
          >
            <Icon
              as={CATEGORY_ICON[slot.category]}
              boxSize={{ base: '25px', md: '28px' }}
              color="grayBlueish"
            />
            <Text
              fontSize={{ base: 'sm', md: 'md' }}
              fontWeight="bold"
              color="grayDark"
              textAlign="center"
              px={2}
            >
              {slot.label}
            </Text>
          </VStack>
        )}

        {!isDragActive && isProcessing && (
          <Flex
            position="absolute"
            inset={0}
            zIndex={2}
            bg={photo ? 'blackAlpha.600' : 'transparent'}
            alignItems="center"
            justifyContent="center"
          >
            <VStack spacing={2}>
              <Spinner color={photo ? 'white' : 'oceanBoatBlue'} size="md" />
              <Text fontSize="xs" color={photo ? 'white' : 'oceanBoatBlue'} fontWeight="700">
                Processing...
              </Text>
            </VStack>
          </Flex>
        )}

        {!isDragActive && photo && (
          <>
            <Image src={photo.previewUrl} alt={slot.label} objectFit="cover" w="100%" h="100%" />

            <Box
              position="absolute"
              bottom={0}
              left={0}
              right={0}
              bgGradient="linear(to-t, blackAlpha.700, transparent)"
              px={2.5}
              pb={2}
              pt={8}
            >
              <Text fontSize="13px" fontWeight="700" color="white" noOfLines={1}>
                {slot.label}
              </Text>
            </Box>

            {!isProcessing && (
              <Flex
                position="absolute"
                inset={0}
                bg="blackAlpha.500"
                opacity={0}
                _hover={{ opacity: 1 }}
                transition="opacity 0.15s ease"
                alignItems="flex-start"
                justifyContent="flex-end"
                p={2}
              >
                {!isPostAcceptance && (
                  <IconButton
                    aria-label="Delete photo"
                    icon={<Icon as={FaTrash} boxSize="14px" />}
                    size="sm"
                    colorScheme="red"
                    borderRadius="full"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete();
                    }}
                  />
                )}
              </Flex>
            )}
          </>
        )}
      </Box>
    </AspectRatio>
  );
};

export default PhotoSlot;
